import * as React from 'react'
import styled from 'styled-components'
import { Button } from '../../UI/Button'
import { NavigationDropdown } from '../NavigationDropdown'
import { SidebarItem } from '../NavigationSidebarItem'
import CustomLink from '../../CustomLink'
import { useI18n } from '../../../lib/context/i18n'

const Styled = {
  Wrapper: styled.div`
    background: ${p => p.theme.colors.white[1]};
    display: flex;
    flex-direction: column;
    height: 100%;
    left: 0;
    opacity: 0.9;
    position: absolute;
    top: 0;
    transform: translateX(100%);
    transition: all 0.3s ease-in-out;
    visibility: hidden;
    width: 100%;
    z-index: 2;

    &.Wrapper--isOpen {
      opacity: 1;
      transform: translateX(0);
      visibility: visible;
    }
  `,
  Header: styled.header`
    align-items: center;
    display: flex;
    gap: 0.25rem;
    padding: 1rem;
  `,
  Title: styled.div`
    flex: 1 1 auto;
  `,
  OuterBody: styled.div`
    flex: 1 1 auto;
    overflow: hidden;
    position: relative;

    &:before,
    &:after {
      content: '';
      height: 6rem;
      left: 0;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      transition: all 0.32s;
      visibility: hidden;
      width: 100%;
      z-index: 20;
    }

    &.OuterBody--atStart {
      &:before {
        opacity: 1;
        visibility: visible;
      }
    }

    &.OuterBody--atEnd {
      &:after {
        opacity: 1;
        visibility: visible;
      }
    }

    &:before {
      top: 0;
      background-image: linear-gradient(to bottom, ${p => p.theme.colors.white[1]}, transparent);
    }

    &:after {
      bottom: 0;
      background-image: linear-gradient(to top, ${p => p.theme.colors.white[1]}, transparent);
    }
  `,
  Body: styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: contain;
  `,
  Groups: styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 1rem 1rem;
  `,
  Sidebar: styled.div`
    background-color: ${({ theme }) => theme.colors.black[7]};
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    gap: 1rem;
    padding: 1rem 1rem;
    min-height: 8rem;
  `,
  CloseButton: styled.div`
    position: absolute;
    top: 1rem;
    right: 1rem;
  `,
}

export interface DropdownSlideProps {
  content: any[]
  isOpen: boolean
  onClose: () => void
  onCloseMenu: () => void
  title: string
}

const GroupItem = ({ group }: { group: any }) => {
  const { getTranslatedUrlFromLink } = useI18n()
  const isLink = !!group.link
  const linkUrl = group.link ? getTranslatedUrlFromLink(group.link) : undefined
  const isExternal = linkUrl && !linkUrl.startsWith('/')

  return (
    <NavigationDropdown.Group theme={group.theme}>
      <NavigationDropdown.GroupHeader
        title={group.title}
        subline={group.description}
        as={isLink ? CustomLink : undefined}
        href={isExternal ? linkUrl : undefined}
        to={!isExternal ? linkUrl : undefined}
      />
      {group.items.map(item => {
        if (item.component === 'LinkWithIcon') {
          return (
            <NavigationDropdown.GroupItem
              key={item._uid}
              label={item.label}
              to={getTranslatedUrlFromLink(item.link)}
              icon={item.icon}
            />
          )
        } else if (item.component === 'Button') {
          console.log(item, 'Button')
          return (
            <Button
              key={item._uid}
              label={item.label}
              to={getTranslatedUrlFromLink(item.link)}
              icon={item.icon}
              iconRight="arrow-right"
              as={CustomLink}
              $ignoreTheme={true}
            />
          )
        }
      })}
    </NavigationDropdown.Group>
  )
}

export const DropdownSlide = ({ isOpen, onClose, onCloseMenu, content, title }: DropdownSlideProps) => {
  const [isAtStart, setIsAtStart] = React.useState(false)
  const [isAtEnd, setIsAtEnd] = React.useState(false)
  const bodyRef = React.useRef<any | null>(null)

  React.useEffect(() => {
    if (bodyRef.current) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = bodyRef.current
        setIsAtStart(scrollTop === 0)
        setIsAtEnd(scrollTop + clientHeight >= scrollHeight)
      }

      handleScroll()
      bodyRef.current.addEventListener('scroll', handleScroll)
      return () => bodyRef.current?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const bodyClasses: string[] = []
  if (!isAtStart) bodyClasses.push('OuterBody--atStart')
  if (!isAtEnd) bodyClasses.push('OuterBody--atEnd')

  return (
    <Styled.Wrapper className={isOpen ? 'Wrapper--isOpen' : ''}>
      <Styled.CloseButton>
        <Button iconLeft="close" onClick={onCloseMenu} $size="small" $variant="tertiary" />
      </Styled.CloseButton>
      <Styled.Header>
        <Button iconLeft="arrow-left" $size="small" $variant="tertiary" $ignoreTheme onClick={onClose} />
        <Styled.Title>{title}</Styled.Title>
      </Styled.Header>
      <Styled.OuterBody className={bodyClasses.join(' ')}>
        <Styled.Body ref={bodyRef}>
          <Styled.Groups>
            {content[0].groups.map(group => (
              <GroupItem key={group._uid} group={group} />
            ))}
          </Styled.Groups>
          {content[0].sidebar_items?.length > 0 && (
            <Styled.Sidebar>
              {content[0].sidebar_items.map(item => (
                <SidebarItem key={item?._uid} item={item} />
              ))}
            </Styled.Sidebar>
          )}
        </Styled.Body>
      </Styled.OuterBody>
    </Styled.Wrapper>
  )
}
